import type {
    Argon2Algorithm,
    Argon2Parameters
} from 'node:crypto';

import { createSalt } from './keygen.ts';
import type { Argon2Hash, Argon2HashParameters } from '../../../types/crypto/argon2';

// Lazy import for argon2 (added in Node.js 24.7.0)
// This allows the module to load on older Node.js versions
let argon2: typeof import('node:crypto').argon2 | undefined;

async function getArgon2() {
    if (argon2 === undefined) {
        try {
            const crypto = await import('node:crypto');
            if (!crypto.argon2) {
                throw new Error('argon2 not available');
            }
            argon2 = crypto.argon2;
        } catch (err) {
            throw new Error(
                'argon2 is not available in this Node.js version. ' +
                'Node.js 24.7.0 or later is required for Argon2 password encoding. ' +
                'Please upgrade Node.js or use a different authentication method.'
            );
        }
    }
    return argon2;
}

// Argon2 version constants
export const ARGON2_VERSION_10 = 0x10; // 16
export const ARGON2_VERSION_13 = 0x13; // 19
export const ARGON2_VERSION = ARGON2_VERSION_13;

// Default parameters for Argon2 hashing
export const DEFAULT_ALGORITHM: Argon2Algorithm = 'argon2id';
export const DEFAULT_SALT_LENGTH = 16;
export const DEFAULT_HASH_LENGTH = 32;
export const DEFAULT_PARALLELISM = 4;
export const DEFAULT_MEMORY = 65536; // 64 MB in KiB
export const DEFAULT_PASSES = 3;

/**
 * Decode an encoded Argon2 hash string into its components.
 * Format: $argon2id$v=19$m=65536,t=3,p=4$base64salt$base64hash
 *
 * @param encodedHash - The encoded Argon2 hash string
 * @returns Decoded hash components
 * @see https://github.com/P-H-C/phc-winner-argon2/blob/master/src/encoding.c#L244
 */
export function decode(encodedHash: string): Argon2Hash {
    const parts = encodedHash.split('$'); // ['', 'argon2id', 'v=19', 'm=65536,t=3,p=4', 'base64salt', 'base64hash']
    if (parts.length < 4) {
        throw new Error('Invalid encoded Argon2 hash');
    }
    let current = 1;
    const algorithm = parts[current++];
    if (algorithm !== 'argon2d' && algorithm !== 'argon2i' && algorithm !== 'argon2id') {
        throw new Error('Invalid Argon2 type');
    }
    let version = ARGON2_VERSION_10;
    if (parts[current].startsWith('v=')) {
        version = parseInt(parts[current].substring(2), 10);
        current++;
    }
    const parameters: Partial<Argon2HashParameters> = {};
    const paramsParts = parts[current++].split(',');

    for (const param of paramsParts) {
        const [key, value] = param.split('=');
        switch (key) {
            case 'm':
                parameters.memory = parseInt(value, 10);
                break;
            case 't':
                parameters.passes = parseInt(value, 10);
                break;
            case 'p':
                parameters.parallelism = parseInt(value, 10);
                break;
        }
    }
    parameters.nonce = Buffer.from(parts[current++], 'base64url');
    const hash = Buffer.from(parts[current++], 'base64url');

    return { algorithm, version, parameters: parameters as Argon2Hash['parameters'], hash };
}

/**
 * Encode Argon2 hash components into a standard string format.
 * Format: $argon2id$v=19$m=65536,t=3,p=4$base64salt$base64hash
 *
 * @param hashData - The hash components to encode
 * @returns Encoded hash string
 */
export function encode(hashData: Argon2Hash): string {
    const { algorithm, version, parameters, hash } = hashData;
    return `$${algorithm}$v=${version}$m=${parameters.memory},t=${parameters.passes},p=${parameters.parallelism}$${parameters.nonce.toString('base64url')}$${hash.toString('base64url')}`;
}


/**
 * Create an Argon2 hash from parameters.
 * Low-level wrapper around node:crypto's argon2.
 *
 * @param algorithm - Argon2 algorithm variant ('argon2d' | 'argon2i' | 'argon2id')
 * @param password - The password to hash
 * @param hashLength - Length of the output hash in bytes
 * @param parameters - Argon2 parameters (nonce, memory, passes, parallelism). If not provided, defaults will be used.
 * @returns The hash buffer
 */
export async function createHash(
    algorithm: Argon2Algorithm,
    password: string,
    hashLength: number,
    parameters?: Argon2Hash['parameters']
): Promise<Buffer> {
    const nonce = parameters?.nonce ?? createSalt(DEFAULT_SALT_LENGTH);
    const memory = parameters?.memory ?? DEFAULT_MEMORY;
    const passes = parameters?.passes ?? DEFAULT_PASSES;
    const parallelism = parameters?.parallelism ?? DEFAULT_PARALLELISM;

    const argon2Params: Argon2Parameters = {
        message: password,
        tagLength: hashLength,
        nonce,
        memory,
        passes,
        parallelism
    };
    const argon2Fn = await getArgon2();

    // Wrap callback-based argon2 in a Promise
    return new Promise((resolve, reject) => {
        argon2Fn(algorithm, argon2Params, (err: Error | null, result?: Uint8Array) => {
            if (err) {
                reject(err);
            }
            else if (result) {
                resolve(Buffer.from(result));
            }
            else {
                reject(new Error('argon2 returned no result'));
            }
        });
    });
}


/**
 * Hash a password using Argon2.
 *
 * @param password - The plain text password to hash
 * @param options - Optional parameters object. If not provided, defaults will be used.
 * @returns The encoded Argon2 hash string
 */
export async function hash(
    password: string,
    options?: {
        algorithm?: Argon2Algorithm;
        saltLength?: number;
        hashLength?: number;
        parallelism?: number;
        memory?: number;
        passes?: number;
    }
): Promise<string> {
    const algorithm = options?.algorithm ?? 'argon2id';
    const saltLength = options?.saltLength ?? DEFAULT_SALT_LENGTH;
    const hashLength = options?.hashLength ?? DEFAULT_HASH_LENGTH;
    const parallelism = options?.parallelism ?? DEFAULT_PARALLELISM;
    const memory = options?.memory ?? DEFAULT_MEMORY;
    const passes = options?.passes ?? DEFAULT_PASSES;

    const nonce = createSalt(saltLength);
    const parameters = { memory, passes, parallelism, nonce };
    const hashBuffer = await createHash(algorithm, password, hashLength, parameters);
    return encode({
        algorithm,
        version: ARGON2_VERSION,
        parameters,
        hash: hashBuffer
    });
}

/**
 * Verify a password against an Argon2 hash.
 *
 * @param encodedHash - The encoded Argon2 hash to verify against
 * @param password - The plain text password to verify
 * @returns true if password matches the hash, false otherwise
 */
export async function verify(encodedHash: string, password: string): Promise<boolean> {
    try {
        const decoded = decode(encodedHash);
        const hashBuffer = await createHash(
            decoded.algorithm,
            password,
            decoded.hash.length,
            decoded.parameters
        );
        return decoded.hash.equals(hashBuffer);
    } catch {
        return false;
    }
}

